import {
    autoUpdate,
    Boundary,
    FloatingFocusManager,
    FloatingPortal,
    FloatingRootContext,
    offset,
    Placement,
    safePolygon,
    shift,
    useFloating,
    useFloatingRootContext,
    useFocus,
    useHover,
    useInteractions,
} from '@floating-ui/react'
import { ReactNode, useState } from 'react'

import { astraTwMerge } from '~/astra'

export type FloatingHoverMenuProps = {
    floatingContext: FloatingRootContext
    setFloating: (floating: HTMLElement | null) => void
    portalRoot?: HTMLElement | null
    placement?: Placement
    boundary?: Boundary
    children: ReactNode
    className?: string
    floatingProps?: Record<string, unknown>
}

export const FloatingHoverMenu = ({
    floatingContext,
    setFloating,
    portalRoot,
    boundary,
    placement = 'top-end',
    children,
    className,
    floatingProps,
}: FloatingHoverMenuProps) => {
    const { floatingStyles } = useFloating({
        rootContext: floatingContext,
        placement,
        strategy: 'absolute',
        middleware: [
            offset({ alignmentAxis: 12, mainAxis: -18 }),
            shift({
                padding: {
                    right: 8,
                    bottom: 8,
                },
                boundary: boundary ?? portalRoot ?? undefined,
                crossAxis: true,
            }),
        ],
        whileElementsMounted: autoUpdate,
    })

    return floatingContext.open ? (
        <FloatingPortal root={portalRoot ?? undefined}>
            <FloatingFocusManager context={floatingContext} initialFocus={-1} modal={false}>
                <div
                    ref={setFloating}
                    style={floatingStyles}
                    className={astraTwMerge(
                        'z-elevated flex items-center rounded-md border border-base bg-surface-base px-1 py-1 text-default shadow-md',
                        className
                    )}
                    {...floatingProps}
                >
                    {children}
                </div>
            </FloatingFocusManager>
        </FloatingPortal>
    ) : null
}

export const useFloatingHoverMenu = () => {
    const [isHoverOpen, setHoverOpen] = useState(false)
    const [reference, setReference] = useState<Element | null>(null)
    const [floating, setFloating] = useState<HTMLElement | null>(null)

    const context = useFloatingRootContext({
        open: isHoverOpen,
        onOpenChange: setHoverOpen,
        elements: {
            reference,
            floating,
        },
    })
    const hover = useHover(context, { handleClose: safePolygon() })
    // also handle focus to open the hover menu, or else keyboard users would never be able to reply
    const focus = useFocus(context)
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus])

    return { context, reference, setReference, setFloating, getReferenceProps, getFloatingProps }
}
